import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Shield,
  Clock,
  Users,
  Wrench,
  FileText,
  BarChart3,
  Phone,
  Building2,
  Briefcase,
  HandCoins,
  Home,
  TrendingUp,
  Award,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { LandlordsSEO } from "../components/SEO";
import StickyValuationCTA from "../components/StickyValuationCTA";
import api from "../lib/api";

// Icon registry — allows admin to set icon names per item from the CMS.
const ICONS = {
  Users,
  Clock,
  BarChart3,
  Shield,
  FileText,
  Wrench,
  CheckCircle2,
  Phone,
  Building2,
  Briefcase,
  HandCoins,
  Home,
  TrendingUp,
  Award,
};

const DEFAULTS = {
  hero_eyebrow: "For Landlords",
  hero_title: "Full Property Management, Done Properly",
  hero_subtitle:
    "Keystone Properties is locally based in Batley and supports landlords across West Yorkshire. We combine strong marketing, thorough tenant checks and proactive property care to help you achieve consistent returns.",
  hero_cta_text: "Book a Free Rental Valuation",
  what_we_do_title: "What We Do for You",
  what_we_do_image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
  what_we_do_items: [
    "Professional marketing & viewings",
    "Tenant referencing & Right to Rent checks",
    "Tenancy setup and documentation",
    "Rent collection & arrears chasing",
    "Maintenance coordination with trusted contractors",
    "Regular inspections & reporting",
    "Compliance guidance (EPC, gas safety, EICR, etc.)",
    "Tenant communication and issue resolution",
  ],
  perfect_for_title: "Perfect For...",
  perfect_for_items: [
    { icon: "Users", title: "First-time Landlords", description: "Complete peace of mind with full support and guidance through every step." },
    { icon: "Clock", title: "Busy Professionals", description: "No time to manage properties? We handle everything for you." },
    { icon: "BarChart3", title: "Portfolio Landlords", description: "Streamlined management across multiple properties." },
  ],
  benefits_items: [
    { icon: "Shield", title: "Full Property Protection", description: "Regular inspections, professional inventory, and responsive maintenance coordination." },
    { icon: "FileText", title: "Compliance Handled", description: "We ensure your property meets all legal requirements—EPC, gas safety, EICR, and more." },
    { icon: "Wrench", title: "Trusted Contractors", description: "Access to our network of reliable, vetted West Yorkshire contractors." },
  ],
  cta_title: "Get a Free Rental Valuation",
  cta_description: "Tell us your property location and type—we'll provide a realistic rental figure and the best management route.",
  cta_button_text: "Book a Free Rental Valuation",
  cta_phone: "01924 520163",
  cta_address: "17 Commercial Street, Batley, WF17 5HJ",
};

const LandlordsPage = () => {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("landlords_page")
      .then((res) => {
        if (mounted && res?.content) {
          setContent({ ...DEFAULTS, ...res.content });
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("Failed to load landlords_page CMS content, using defaults:", err?.message);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const renderIcon = (name, className) => {
    const Icon = ICONS[name] || Shield;
    return <Icon className={className} />;
  };

  return (
    <div data-testid="landlords-page">
      <LandlordsSEO />
      <StickyValuationCTA source="landlords-page" />
      {/* Hero Section */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              {content.hero_eyebrow}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              {content.hero_title}
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">{content.hero_subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="bg-gold hover:bg-gold-light text-navy font-semibold"
                data-testid="landlords-cta-valuation"
              >
                <Link to="/contact">
                  {content.hero_cta_text}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                data-testid="landlords-cta-view-packages"
              >
                <Link to="/services/packages">
                  View Packages &amp; Pricing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-8">
                {content.what_we_do_title}
              </h2>
              <div className="space-y-4" data-testid="landlords-what-we-do">
                {(content.what_we_do_items || []).map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={content.what_we_do_image}
                alt={content.what_we_do_title}
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              {content.perfect_for_title}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-testid="landlords-perfect-for">
            {(content.perfect_for_items || []).map((item, index) => (
              <Card key={item.title || index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-6">
                    {renderIcon(item.icon, "w-8 h-8 text-navy")}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Callout */}
      <section className="py-12 md:py-16 bg-white" data-testid="landlords-packages-callout">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-navy via-navy to-navy-light p-8 md:p-12">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-gold/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <span className="text-gold font-medium text-xs sm:text-sm tracking-wider uppercase mb-3 block">
                  Find Your Service Level
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3 leading-tight">
                  Bronze, Silver, Gold &amp; Platinum Packages
                </h3>
                <p className="text-white/75 leading-relaxed">
                  Whether you're a first-time landlord or running a portfolio, we have a tailored package — from tenant-find only to fully managed HMOs.
                </p>
              </div>
              <div className="md:text-right">
                <Button
                  asChild
                  size="lg"
                  className="bg-gold hover:bg-gold-light text-navy font-semibold w-full md:w-auto"
                  data-testid="landlords-packages-callout-cta"
                >
                  <Link to="/services/packages">
                    View Packages
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8" data-testid="landlords-benefits">
            {(content.benefits_items || []).map((benefit, index) => (
              <Card key={benefit.title || index} className="border-slate-200">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-lg bg-gold/10 flex items-center justify-center mb-6">
                    {renderIcon(benefit.icon, "w-7 h-7 text-gold")}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
              {content.cta_title}
            </h2>
            <p className="text-white/80 text-lg mb-8">{content.cta_description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gold hover:bg-gold-light text-navy font-semibold"
                data-testid="landlords-cta-bottom"
              >
                <Link to="/contact">
                  {content.cta_button_text}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-white/70 text-sm">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href={`tel:${(content.cta_phone || "").replace(/\s/g, "")}`} className="hover:text-gold">
                  {content.cta_phone}
                </a>
              </div>
              <span className="hidden sm:inline">•</span>
              <span>{content.cta_address}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandlordsPage;
